'use client'

import {
    Hospital,
    getHospitalsByOrganization,
} from '@/lib/api/hospitals/hospital-api'
import { useAuth } from '@/store/hooks'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { MdClose, MdSearch } from 'react-icons/md'
import { FaHospital } from 'react-icons/fa6'
import { useAnchoredDropdown } from '@/components/use-anchored-dropdown'

type BaseProps = {
    label?: string
    placeholder?: string
    disabled?: boolean
    className?: string
    allowedIds?: number[] | null
    emptyText?: string
    /** `sm` para uso dentro de linha de tabela, onde o campo padrão não cabe. */
    size?: 'sm' | 'md'
}

type SingleProps = BaseProps & {
    multi?: false
    value: number | null
    onChange: (hospitalId: number | null, hospital: Hospital | null) => void
}

type MultiProps = BaseProps & {
    multi: true
    value: number[]
    onChange: (hospitalIds: number[], hospitals: Hospital[]) => void
}

type Props = SingleProps | MultiProps

function getInitials(name: string) {
    return name
        .split(' ')
        .map(n => n[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
}

function getAvatarColor(id: number) {
    const colors = [
        'bg-blue-500',
        'bg-green-500',
        'bg-purple-500',
        'bg-orange-500',
        'bg-pink-500',
        'bg-cyan-500',
        'bg-indigo-500',
        'bg-teal-500',
    ]
    return colors[id % colors.length]
}

export function HospitalSearchSelector(props: Props) {
    const {
        label = 'Hospital',
        placeholder = 'Buscar hospital...',
        disabled = false,
        className = '',
        allowedIds = null,
        emptyText,
        size = 'md',
    } = props
    const sm = size === 'sm'

    const { user } = useAuth()
    const [allHospitals, setAllHospitals] = useState<Hospital[]>([])
    const [loading, setLoading] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [showDropdown, setShowDropdown] = useState(false)
    const [selectedSingle, setSelectedSingle] = useState<Hospital | null>(null)
    const [selectedMulti, setSelectedMulti] = useState<Hospital[]>([])
    const containerRef = useRef<HTMLDivElement>(null)

    const loadHospitals = useCallback(async () => {
        if (!user?.organizationId) return
        setLoading(true)
        try {
            const response = await getHospitalsByOrganization(
                user.organizationId
            )
            if (response.success) {
                setAllHospitals(
                    Array.isArray(response.result) ? response.result : []
                )
            }
        } catch {
            // silencioso
        } finally {
            setLoading(false)
        }
    }, [user?.organizationId])

    useEffect(() => {
        loadHospitals()
    }, [loadHospitals])

    // Sync selection state a partir do value externo
    useEffect(() => {
        if (props.multi) {
            const ids = props.value || []
            const found = allHospitals.filter(h => ids.includes(h.id))
            // Manter ordem dos ids recebidos
            const ordered = ids
                .map(id => found.find(h => h.id === id))
                .filter((h): h is Hospital => !!h)
            // Incluir registros ainda não carregados como placeholder (id-only) para evitar sumiço
            const missing = ids
                .filter(id => !found.some(h => h.id === id))
                .map(
                    id =>
                        ({
                            id,
                            name: `Hospital #${id}`,
                            active: true,
                            organizationId: user?.organizationId || 0,
                            createdAt: '',
                            updatedAt: '',
                        }) as Hospital
                )
            setSelectedMulti([...ordered, ...missing])
        } else {
            const v = props.value
            if (v == null) {
                setSelectedSingle(null)
            } else {
                const found = allHospitals.find(h => h.id === v)
                if (found) setSelectedSingle(found)
                else if (!selectedSingle || selectedSingle.id !== v) {
                    setSelectedSingle({
                        id: v,
                        name: `Hospital #${v}`,
                        active: true,
                        organizationId: user?.organizationId || 0,
                        createdAt: '',
                        updatedAt: '',
                    } as Hospital)
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.value, allHospitals])

    // Fechar-ao-clicar-fora mora no hook: com o painel em portal, ele não está mais
    // dentro do container e o handler antigo o trataria como clique de fora, fechando
    // antes de a opção ser escolhida.
    const closeDropdown = useCallback(() => setShowDropdown(false), [])
    const { anchorRef, renderPanel } = useAnchoredDropdown(
        showDropdown && (props.multi || !selectedSingle),
        closeDropdown
    )

    const filteredResults = useMemo(() => {
        const term = searchTerm.trim().toLowerCase()
        let list = allHospitals
        if (allowedIds && allowedIds.length > 0) {
            list = list.filter(h => allowedIds.includes(h.id))
        }
        if (term) {
            list = list.filter(
                h =>
                    h.name.toLowerCase().includes(term) ||
                    (h.tradingName || '').toLowerCase().includes(term) ||
                    (h.cnpj || '').includes(term)
            )
        }
        if (props.multi) {
            const selectedIds = selectedMulti.map(h => h.id)
            list = list.filter(h => !selectedIds.includes(h.id))
        }
        return list
    }, [allHospitals, searchTerm, allowedIds, props.multi, selectedMulti])

    const handleSelect = (hospital: Hospital) => {
        if (props.multi) {
            const next = [...selectedMulti, hospital]
            setSelectedMulti(next)
            props.onChange(
                next.map(h => h.id),
                next
            )
            setSearchTerm('')
        } else {
            setSelectedSingle(hospital)
            props.onChange(hospital.id, hospital)
            setShowDropdown(false)
            setSearchTerm('')
        }
    }

    const handleRemoveMulti = (id: number) => {
        if (!props.multi) return
        const next = selectedMulti.filter(h => h.id !== id)
        setSelectedMulti(next)
        props.onChange(
            next.map(h => h.id),
            next
        )
    }

    const handleClearSingle = () => {
        if (props.multi) return
        setSelectedSingle(null)
        props.onChange(null, null)
        setSearchTerm('')
    }

    const handleFocus = () => {
        setShowDropdown(true)
    }

    const subtitle = (h: Hospital) => {
        const parts: string[] = []
        if (h.tradingName) parts.push(h.tradingName)
        if (h.city && h.state) parts.push(`${h.city}/${h.state}`)
        else if (h.city) parts.push(h.city)
        return parts.join(' - ')
    }

    return (
        <div className={`form-control w-full ${className}`} ref={containerRef}>
            {label && (
                <label className="label">
                    <span
                        className={`label-text ${sm ? 'text-xs' : ''} font-medium`}
                    >
                        {label}
                    </span>
                </label>
            )}

            <div className="relative">
                {!props.multi && selectedSingle ? (
                    <div
                        className={`flex items-center gap-2 input input-bordered w-full pr-10 ${sm ? 'input-xs' : ''}`}
                    >
                        <div
                            className={`rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 ${sm ? 'w-4 h-4 text-[9px]' : 'w-6 h-6 text-xs'} ${getAvatarColor(selectedSingle.id)}`}
                        >
                            {getInitials(selectedSingle.name)}
                        </div>
                        <span className={`truncate ${sm ? 'text-xs' : ''}`}>
                            {selectedSingle.name}
                            {selectedSingle.tradingName
                                ? ` - ${selectedSingle.tradingName}`
                                : ''}
                        </span>
                        {!disabled && (
                            <button
                                type="button"
                                onClick={handleClearSingle}
                                className="absolute right-3 top-1/2 -translate-y-1/2 btn btn-ghost btn-xs btn-circle"
                            >
                                <MdClose className="text-lg" />
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        {props.multi && selectedMulti.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                                {selectedMulti.map(h => (
                                    <span
                                        key={h.id}
                                        className="badge badge-secondary gap-2 py-3 pl-1 pr-2"
                                    >
                                        <span
                                            className={`w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-bold ${getAvatarColor(h.id)}`}
                                        >
                                            {getInitials(h.name)}
                                        </span>
                                        <span className="text-xs">
                                            {h.name}
                                        </span>
                                        {!disabled && (
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handleRemoveMulti(h.id)
                                                }
                                                className="hover:bg-primary-focus rounded-full p-0.5"
                                                aria-label={`Remover ${h.name}`}
                                            >
                                                <MdClose className="text-sm" />
                                            </button>
                                        )}
                                    </span>
                                ))}
                            </div>
                        )}
                        <div className="relative" ref={anchorRef}>
                            <MdSearch
                                className={`absolute z-10 top-1/2 -translate-y-1/2 text-base-content/50 pointer-events-none ${sm ? 'left-2.5 text-base' : 'left-3 text-xl'}`}
                            />
                            <input
                                type="text"
                                placeholder={placeholder}
                                className={`input input-bordered w-full ${sm ? 'input-sm pl-9' : 'pl-10'}`}
                                value={searchTerm}
                                onChange={e => {
                                    setSearchTerm(e.target.value)
                                    setShowDropdown(true)
                                }}
                                onFocus={handleFocus}
                                disabled={disabled}
                                autoComplete="off"
                                aria-autocomplete="none"
                            />
                        </div>
                    </>
                )}

                {renderPanel(
                    <>
                        {loading ? (
                            <div className="p-4 text-center">
                                <span className="loading loading-spinner loading-sm"></span>
                            </div>
                        ) : filteredResults.length === 0 ? (
                            <div className="p-4 text-center text-base-content/60 text-sm">
                                <FaHospital className="text-3xl mx-auto mb-2 opacity-50" />
                                <p>
                                    {emptyText ||
                                        (searchTerm
                                            ? 'Nenhum hospital encontrado'
                                            : allowedIds &&
                                                allowedIds.length === 0
                                              ? 'Nenhum hospital vinculado ao item'
                                              : 'Nenhum hospital cadastrado')}
                                </p>
                            </div>
                        ) : (
                            filteredResults.map(hospital => (
                                <button
                                    key={hospital.id}
                                    type="button"
                                    className="w-full px-4 py-3 text-left hover:bg-base-200 flex items-center gap-3 border-b border-base-200 last:border-b-0"
                                    onClick={() => handleSelect(hospital)}
                                >
                                    <div
                                        className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0 ${getAvatarColor(hospital.id)}`}
                                    >
                                        {getInitials(hospital.name)}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="font-medium truncate">
                                            {hospital.name}
                                        </p>
                                        <p className="text-xs text-base-content/60 truncate">
                                            {subtitle(hospital)}
                                        </p>
                                    </div>
                                </button>
                            ))
                        )}
                    </>
                )}
            </div>
        </div>
    )
}
